/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import {
  Amounts,
  HttpStatusCode,
  TalerError,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Attention,
  Loading,
  RouteDefinition,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { RetryableError } from "../components/RetryableError.js";
import {
  revalidateAccountDetails,
  useAccountDetails,
} from "../hooks/account.js";
import { IntAmounts } from "./regional/CreateCashout.js";
import { WalletWithdrawForm } from "./WalletWithdrawForm.js";

export function WalletWithdrawal({
  account,
  routeCancel,
  onOperationCreated,
}: {
  account: string;
  routeCancel: RouteDefinition;
  onOperationCreated(operationId: string): void;
}): VNode {
  const { i18n } = useTranslationContext();
  const result = useAccountDetails(account);
  if (!result) return <Loading />;
  if (result instanceof TalerError) {
    return (
      <RetryableError
        error={result}
        title={i18n.str`Failed to load account details.`}
        onRetry={() => void revalidateAccountDetails()}
      />
    );
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.Unauthorized:
      case HttpStatusCode.NotFound:
        return (
          <Attention
            type="danger"
            title={i18n.str`Unable to start a withdrawal.`}
          >
            <i18n.Translate>Please sign in again.</i18n.Translate>
          </Attention>
        );
      default:
        assertUnreachable(result);
    }
  }
  const amount = Amounts.parseOrThrow(result.body.balance.amount);
  const isDebit = result.body.balance.credit_debit_indicator === "debit";
  const signed = IntAmounts.toIntAmount(amount, isDebit);
  const debtLimit = Amounts.parseOrThrow(result.body.debit_threshold);
  const limit = signed.increment(debtLimit).result;

  return (
    <section class="mt-2">
      <a
        href={routeCancel.url({})}
        class="inline-flex items-center text-sm font-semibold text-primaryDark hover:underline"
      >
        <span class="mr-1" aria-hidden="true">
          ←
        </span>
        <i18n.Translate>Back to overview</i18n.Translate>
      </a>
      <WalletWithdrawForm
        limit={limit}
        debtLimit={debtLimit}
        onOperationCreated={onOperationCreated}
        onOperationAborted={() => undefined}
        routeCancel={routeCancel}
      />
    </section>
  );
}
